import CostAllocation from "./nc/CostAllocation";
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Spinner,
  FormControl,
  InputGroup,
  ButtonGroup,
} from "react-bootstrap";
import { authFetch } from "./utils/authFetch"; // ✅ path corect pentru componenta ta

function recTypeLabel(recType) {
  if (typeof recType === "number")
    return recType === 2 ? "NIR" : recType === 0 ? "INTRĂRI" : String(recType);
  const s = String(recType).toLowerCase();
  return s === "nir" ? "NIR" : s === "intrari" ? "INTRĂRI" : s.toUpperCase();
}

export default function ViewNotaModal({ recType, docId, show, onHide }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [viewMode, setViewMode] = useState("articole"); // implicit "articole" pentru afisare simplificata de forma cont_debit - cont_credit - suma

  useEffect(() => {
    if (!show) return;

    const rt =
      typeof recType === "number" ? recType : String(recType).toLowerCase();

    // ✅ IMPORTANT: path relativ (authFetch știe baza /api)
    const url = `/services/nota?rec_type=${rt}&doc_id=${docId}`;

    setLoading(true);
    setError(null);

    authFetch(url)
      .then((data) => setRows(Array.isArray(data) ? data : data?.items || []))
      .catch((e) => setError(e?.message ?? "Eroare la încărcare"))
      .finally(() => setLoading(false));
  }, [show, recType, docId]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;

    return rows.filter((r) =>
      [
        r.account ?? "",
        r.accountcoresp ?? "",
        r.bxexplic ?? "",
        r.partner ?? "",
        r.description ?? "",
      ].some((s) =>
        String(s || "")
          .toLowerCase()
          .includes(qq),
      ),
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    const debit = filtered.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    const credit = filtered.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    return { debit, credit, diff: +(debit - credit).toFixed(2) };
  }, [filtered]);

  const displayRows = useMemo(() => {
    if (viewMode === "articole") {
      // Articole contabile reprezintă formulele unice D - C obținute din liniile de debit
      return filtered.filter((r) => (Number(r.debit) || 0) !== 0);
    }
    return filtered;
  }, [filtered, viewMode]);

  const title = `${recTypeLabel(recType)} #${docId}`;
  const balanced = totals.diff === 0;

  function formatDateRo(val) {
    if (!val) return "";
    if (val instanceof Date && !isNaN(val))
      return val.toLocaleDateString("ro-RO");

    const s = String(val);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;

    const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z";
    const d = new Date(iso);
    return isNaN(d) ? s : d.toLocaleDateString("ro-RO");
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Nota contabilă {title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <InputGroup style={{ maxWidth: "450px" }}>
            <FormControl
              placeholder="Filtrează după cont, partener sau explicație..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button variant="outline-secondary" onClick={() => setQ("")}>
              Reset
            </Button>
          </InputGroup>

          <ButtonGroup size="sm">
            <Button
              variant={viewMode === "articole" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("articole")}
            >
              Articole Contabile (D - C - Sumă)
            </Button>
            <Button
              variant={viewMode === "jurnal" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("jurnal")}
            >
              Registru Jurnal
            </Button>
          </ButtonGroup>
        </div>

        {loading ? (
          <div className="py-3 d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> Se încarcă…
          </div>
        ) : error ? (
          <div className="text-danger py-2">{error}</div>
        ) : (
          <div className="table-responsive">
            <Table striped hover size="sm">
              <thead>
                {viewMode === "articole" ? (
                  <tr>
                    <th>Data</th>
                    <th>Formulă contabilă (Debit - Credit)</th>
                    <th>Explicație</th>
                    <th className="text-end">Sumă</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Data</th>
                    <th>Cont</th>
                    <th>Explicație</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                    <th>Cont corespondent</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {viewMode === "articole" ? (
                  displayRows.map((r) => (
                    <tr key={r.id ?? `${r.account}-${r.debit}-${r.data_nc}`}>
                      <td>{formatDateRo(r.data_nc)}</td>
                      <td>
                        <strong className="text-primary">{r.account}</strong>
                        {" - "}
                        <strong className="text-danger">{r.accountcoresp}</strong>
                      </td>
                      <td>{r.bxexplic || ""}</td>
                      <td className="text-end font-monospace fw-bold">
                        {Number(r.debit || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  displayRows.map((r) => (
                    <tr
                      key={
                        r.id ?? `${r.account}-${r.debit}-${r.credit}-${r.data_nc}`
                      }
                    >
                      <td>{formatDateRo(r.data_nc)}</td>
                      <td>
                        <code>{r.account}</code>
                      </td>
                      <td>{r.bxexplic || ""}</td>
                      <td className="text-end">
                        {Number(r.debit || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="text-end">
                        {Number(r.credit || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td>{r.accountcoresp || ""}</td>
                    </tr>
                  ))
                )}

                {displayRows.length === 0 && (
                  <tr>
                    <td colSpan={viewMode === "articole" ? 4 : 6} className="text-center text-muted py-3">
                      Nu există linii în nota contabilă.
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                {viewMode === "articole" ? (
                  <tr>
                    <td colSpan={3}>
                      <strong>Total Sumă</strong>
                    </td>
                    <td className="text-end">
                      <strong>
                        {totals.debit.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </strong>
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td colSpan={3}>
                        <strong>Total</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {totals.debit.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {totals.credit.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </strong>
                      </td>
                      <td />
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <strong>Diferență (D − C)</strong>
                      </td>
                      <td
                        colSpan={3}
                        className={
                          "text-end " + (balanced ? "text-success" : "text-danger")
                        }
                      >
                        <strong>
                          {totals.diff.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </strong>
                      </td>
                    </tr>
                  </>
                )}
              </tfoot>
            </Table>
          </div>
        )}
        {show && !loading && !error && <CostAllocation recType={recType} docId={docId} />}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
